import { FC, memo, useState, useMemo, useEffect } from 'react';
import { Button, Grid, TextInput, Typography } from '@ts/storybook';
import { Icon, Tooltip } from '@components/Outputs';
import WrapperEntitySelector from '@modules/entitySelector/WrapperEntitySelector';
import { generateSearchEntitySelector } from '@modules/entitySelector/entitySelector.utils';
import { CDevice, CDeviceType, CEntity, CLocation, IEntity, IForm } from '@ts/ui-models';
import { isEqual } from 'lodash';
import Misc from '@components/Misc';
import { makeImpinjxSpanStrategy, parseAntennaId } from './InterfaceListNormalize';
import { useEffectOnce } from 'react-use';
import useEffectDebounced from '@utils/hooks/useEffectDebounced';

export interface InterfaceType {
  logicalId: string;
  name: string;
  isLocable?: boolean;
  isLinkable?: boolean;
}

export interface DeviceTypeInterface {
  logicalId: string;
  name: string;
  interfaceType: InterfaceType;
  details?: {
    comments?: string;
  };
}

export interface DeviceInterface {
  deviceTypeInterfaceId: string;
  customId?: string;
  linkedDeviceInterface?: CDeviceType;
  linkedDeviceInterfaceId?: string;
  locationId?: string;
  location?: CLocation;
}

export interface InterfacesListProps extends IForm {
  t: (key: string) => string;
  values: CDevice;
}

//This component has a problem with mounting and dismounting the problem comes from above the DeviceForm along with the tab that dominates it, long time to fix this, small fix done.
const InterfacesList: FC<InterfacesListProps> = memo(
  props => {
    const { t, values: device, onCallbackChange, mode } = props;
    const isViewMode = mode === 'view';
    const deviceTypeInterfaces = device.deviceType.deviceTypeInterface;
    const [deviceInterfaces, setDeviceInterfaces] = useState<any[]>(device.deviceInterfaces);

    const isDeviceTypeImpinjxSpan = device.deviceType.logicalId === 'impinjxSpan';

    const [showLinked, setShowLinked] = useState<string[]>(
      deviceInterfaces.filter(di => di.linkedDeviceInterfaceId).map(di => di.deviceTypeInterfaceId)
    );

    const [showLocation, setShowLocation] = useState<string[]>(
      deviceInterfaces.filter(di => di.locationId).map(di => di.deviceTypeInterfaceId)
    );

    //#region Handlers
    const onChangeDeviceInterfaceValue = (id: string) => (value: string | number) => {
      const targets = getTargetsForUpdate(id);
      const updated = upsertInterfaces(deviceInterfaces, targets, String(value));
      setDeviceInterfaces(updated);
    };

    const onChangeDeviceInterfaceEntity = (id: string, field: keyof DeviceInterface | string) => (entity: any) => {
      const targets = getTargetsForUpdate(id);
      const updated = upsertInterfacesField(deviceInterfaces, targets, field, entity);
      setDeviceInterfaces(updated);
    };

    const onChangeLinkedDeviceInterface = (id: string) => (entity: any) =>
      onChangeDeviceInterfaceEntity(id, 'linkedDeviceInterface')(entity);

    const onChangeLocationDeviceInterface = (id: string) => (entity: any) =>
      onChangeDeviceInterfaceEntity(id, 'location')(entity);

    const onChangeLocationButton = (id: string) => () => {
      if (showLocation.includes(id)) {
        setShowLocation(showLocation.filter(item => item !== id));
        onChangeLocationDeviceInterface(id)(null);
      } else {
        setShowLocation([...showLocation, id]);
      }
    };

    const onChangeLinkedButton = (id: string) => () => {
      if (showLinked.includes(id)) {
        setShowLinked(showLinked.filter(item => item !== id));
        onChangeLinkedDeviceInterface(id)(null);
      } else {
        setShowLinked([...showLinked, id]);
      }
    };
    //#endregion

    //#region Funtions
    const getDeviceInterface = (id: string): DeviceInterface | undefined =>
      deviceInterfaces.find(di => di.deviceTypeInterfaceId === id);

    const getDeviceInterfaceValue = (id: string): string => {
      const di = getDeviceInterface(id);
      return di?.customId || '';
    };

    const getTargetsForUpdate = (id: string): string[] => {
      if (!isDeviceTypeImpinjxSpan) return [id];
      return makeImpinjxSpanStrategy().getTargetsForUpdate(id);
    };

    const upsertInterfaces = (list: DeviceInterface[], targetIds: string[], customId: string): DeviceInterface[] => {
      const next = [...list];
      const indexById = new Map(next.map((di, i) => [di.deviceTypeInterfaceId, i] as const));
      for (const targetId of targetIds) {
        const idx = indexById.get(targetId);
        if (idx != null) {
          next[idx] = { ...next[idx], customId };
        } else {
          next.push({ deviceTypeInterfaceId: targetId, customId });
          indexById.set(targetId, next.length - 1);
        }
      }
      return next;
    };

    const upsertInterfacesField = (
      list: DeviceInterface[],
      targetIds: string[],
      field: keyof DeviceInterface | string,
      entity: CEntity<IEntity> | null
    ): DeviceInterface[] => {
      const next = [...list];
      const indexById = new Map(next.map((di, i) => [di.deviceTypeInterfaceId, i] as const));

      const isIdField = typeof field === 'string' && field.endsWith('Id');
      const idField = isIdField ? String(field) : `${String(field)}Id`;
      const entityId = entity?.getID() ?? null;

      for (const targetId of targetIds) {
        let idx = indexById.get(targetId);
        if (idx == null) {
          next.push({ deviceTypeInterfaceId: targetId, customId: '' });
          idx = next.length - 1;
          indexById.set(targetId, idx);
        }
        const patch: Record<string, any> = {};
        if (!isIdField) patch[String(field)] = entity ?? null;
        patch[idField] = entity ? entityId : null;
        next[idx] = { ...next[idx], ...patch };
      }
      return next;
    };

    const getInterfaceFormatHint = (interfaceTypeId: string): string => {
      switch (interfaceTypeId) {
        case 'BLE_iBeaconUUID':
          return `40 ${t('hexadecimal uppercase characters')} (FB0B57A2822844CD913A94A122BA1206000100002)`;
        case 'BLE_ALTBeaconUUID':
          return `40 ${t('hexadecimal uppercase characters')} (EA3E469E9441F35E764F9C324B7905B80812409C)`;
        case 'BLE_eddystoneUUID':
          return `32 ${t('hexadecimal uppercase characters')} (
          (EDD1EBEAC04E5DEFA017707CC5B49834)
        )`;
        case 'BLE_AdvAddr':
          return `12 ${t('hexadecimal uppercase characters')} (0A623669)`;
        case 'LoRa_DevEUI':
          return `16 ${t('hexadecimal uppercase characters')} (000DB5360A623669)`;
        case 'LoRa_GatewayEUI':
          return `16 ${t('hexadecimal uppercase characters')} (73A56C978C839864)`;
        case 'LoRa_DevAddr':
          return `8 ${t('hexadecimal uppercase characters')}, ${t('format is given by the device')}`;
        case 'LoRa_AppEUI':
          return ``;
        case 'WiFi_MACAddr':
        case 'LAN_MACAddr':
        case '3G_MACAddr':
        case '4G_MACAddr':
          return `12 ${t('hexadecimal uppercase characters')} (1D83F47342A5)`;
        case 'WiFi_IPAddr':
        case 'LAN_IPAddr':
        case '3G_IPAddr':
        case '4G_IPAddr':
          return `${t('Quad-dotted 32-bit decimal number')} (192.168.1.123)`;
        case '3G_IMEI':
          return `15 ${t('numeric digits')} (490154203237518)`;
        case '3G_telephone':
          return `${t('Telephone number')} (+49 619756978)`;
        case '4G_IMEI':
          return `15 ${t('numeric digits')} (302719594425670)`;
        case 'UWB_MACAddr':
          return `16 ${t('hexadecimal uppercase characters')} (1D8743F478C342A5)`;
        case 'RFID_EPC':
          return `24 ${t('uppercase characters')} (011234567891011001122DBC)`;
        case 'RFID_tagId':
          return `24 ${t('uppercase characters')} (4F422275334835423F532C35)`;
        case 'RFID_antennaPort':
          return t('Integer number from 1 to 32');
        case 'barcode_UPC':
          return `12 ${t('numeric digits')} (725272730708)`;
        case 'QRCode_QRCode':
          return ``;
        case 'NFC_NFCId':
          return `14 ${t('hexadecimal uppercase characters')} (043A98CABB2B80)`;
        default:
          return '';
      }
    };
    //#endregion

    //#region Render
    const renderDeviceInterfaceName = (deviceTypeInterface: DeviceTypeInterface) => {
      const { name, interfaceType } = deviceTypeInterface;
      return (
        <TextInput
          key={interfaceType.logicalId}
          isReadOnly
          label="Interface"
          value={name}
          infoText={getInterfaceFormatHint(interfaceType.logicalId)}
        />
      );
    };

    const renderDeviceInterfaceValue = (deviceTypeInterface: DeviceTypeInterface) => {
      const id = deviceTypeInterface.logicalId;
      const comment = deviceTypeInterface.details?.comments;

      const autoValue = parseAntennaId(id)?.n != null ? String(parseAntennaId(id)!.n) : '';
      const isImpinjAndAntennaPort = isDeviceTypeImpinjxSpan && id.includes('antennaPort');
      const value = isImpinjAndAntennaPort ? autoValue : getDeviceInterfaceValue(id);
      return (
        <TextInput
          key={id}
          label="ID"
          value={value}
          isReadOnly={isViewMode}
          disabled={!isViewMode && isImpinjAndAntennaPort}
          onChangeValue={onChangeDeviceInterfaceValue(id)}
          infoText={comment}
        />
      );
    };

    const renderButtons = (deviceTypeInterface: DeviceTypeInterface) => {
      const { interfaceType, logicalId } = deviceTypeInterface;
      const isLocable = interfaceType.isLocable;
      const fDisabledButton = !getDeviceInterfaceValue(logicalId) || isViewMode;
      return <>{isLocable && renderLocationButton(logicalId, fDisabledButton)}</>;
    };

    const renderLocationButton = (id: string, disabled: boolean) => {
      const di = getDeviceInterface(id);
      const locationId = di?.locationId;
      const title = locationId ? 'Unassign location' : 'Assign location';

      return (
        <Tooltip disabled={disabled} title={title}>
          <Button disabled={disabled} onClick={onChangeLocationButton(id)} variant="outlined">
            <Icon icon={showLocation.includes(id) ? 'LocationOff' : 'LocationOn'} />
          </Button>
        </Tooltip>
      );
    };

    const renderButtonEntitySelectors = (deviceTypeInterface: DeviceTypeInterface) => {
      const id = deviceTypeInterface.logicalId;
      return (
        <>
          {showLinked.includes(id) && renderLinkedDeviceInterface(id)}
          {showLocation.includes(id) && renderLocationDeviceInterface(id)}
        </>
      );
    };

    const renderLinkedDeviceInterface = (id: string) => {
      const linked = getDeviceInterface(id);
      const searchComponent = () => {
        const config = generateSearchEntitySelector('deviceInterface');
        return {
          component: config.component,
          configComponent: {
            ...config.configComponent,
            fetchURL: `/api/deviceInterfaces/${device.logicalId}/${id}`,
          },
        };
      };

      return (
        <WrapperEntitySelector
          key={id}
          label="Device interfaces"
          searchComponent={searchComponent()}
          isReadOnly={isViewMode}
          value={linked?.linkedDeviceInterface ?? linked?.linkedDeviceInterfaceId}
          onChangeEntity={onChangeLinkedDeviceInterface(id)}
        />
      );
    };

    const renderLocationDeviceInterface = (id: string) => {
      const di = getDeviceInterface(id);
      return (
        <WrapperEntitySelector
          key={id}
          label="Assigned location"
          searchComponent={generateSearchEntitySelector('location')}
          isReadOnly={isViewMode}
          value={di?.location ?? di?.locationId}
          onChangeEntity={onChangeLocationDeviceInterface(id)}
        />
      );
    };

    //#endregion

    const collator = useMemo(() => new Intl.Collator('es', { sensitivity: 'base', numeric: true }), []);

    const sortedInterfaces = useMemo(
      () => [...deviceTypeInterfaces].sort((a, b) => collator.compare(String(a.logicalId ?? ''), String(b.logicalId ?? ''))),
      [deviceTypeInterfaces, collator]
    );

    useEffectDebounced(() => {
      if (isEqual(deviceInterfaces, device.deviceInterfaces)) return;
      if (isDeviceTypeImpinjxSpan) {
        onCallbackChange('deviceInterfaces')(
          [...deviceInterfaces].map(di => {
            const autoValue =
              parseAntennaId(di.deviceTypeInterfaceId)?.n != null
                ? String(parseAntennaId(di.deviceTypeInterfaceId)!.n)
                : di.customId;
            return { ...di, customId: autoValue };
          })
        );
        setShowLocation(deviceInterfaces.filter(di => di.locationId).map(di => di.deviceTypeInterfaceId));
      } else {
        onCallbackChange('deviceInterfaces')(deviceInterfaces);
      }
    }, [deviceInterfaces]);

    useEffectOnce(() => {
      if (isDeviceTypeImpinjxSpan && mode === 'new') {
        setDeviceInterfaces(
          deviceTypeInterfaces.map(di => {
            const autoValue = parseAntennaId(di.id)?.n != null ? String(parseAntennaId(di.id)!.n) : di.customId;
            return { ...di, deviceTypeInterfaceId: di.id, customId: autoValue, logicalId: null, id: null };
          })
        );
      }
    });

    return (
      <Misc.NewPaperWithPadding>
        <Typography variant="h5" gutterBottom text={[<b>Interfaces</b>]} />
        <Grid container alignItems="flex-start">
          <Grid container size={{ xs: 12 }}>
            {sortedInterfaces.map(deviceTypeInterface => {
              const id = deviceTypeInterface.logicalId;
              return (
                <Grid container size={{ xs: 12 }} spacing={2} key={id} alignItems="center" marginTop={2}>
                  <Grid size={{ xs: 12, md: 'auto' }}>{renderDeviceInterfaceName(deviceTypeInterface)}</Grid>
                  <Grid size={{ xs: 10, md: 5 }}>{renderDeviceInterfaceValue(deviceTypeInterface)}</Grid>
                  {!isViewMode && <Grid size={{ xs: 'auto' }}>{renderButtons(deviceTypeInterface)}</Grid>}
                  <Grid size={{ xs: 3 }}>{renderButtonEntitySelectors(deviceTypeInterface)}</Grid>
                </Grid>
              );
            })}
          </Grid>
        </Grid>
      </Misc.NewPaperWithPadding>
    );
  },
  (prevProps, nextProps) => {
    return (
      isEqual(prevProps.values.deviceInterfaces, nextProps.values.deviceInterfaces) && isEqual(prevProps.mode, nextProps.mode)
    );
  }
);

export default InterfacesList;
